import {
  ApiResponse,
  BulkInvitationResult,
  InvitationEmailData,
  InvitationResponse,
  OnboardingUserData,
  ProjectInvitation,
  SendInvitationData,
  User,
} from '@/types';
import { api } from './api';

class InvitationService {
  private baseUrl = '/invitations';

  /**
   * Send invitations to multiple email addresses
   */
  async sendInvitations(data: SendInvitationData): Promise<ApiResponse<BulkInvitationResult>> {
    try {
      // Merge provided emailStatuses with fresh email checks
      let emailStatuses = data.emailStatuses || {};

      // Only check emails that don't have provided status
      const emailsToCheck = data.emails.filter(email => !emailStatuses[email]);

      if (emailsToCheck.length > 0) {
        const emailChecks = await this.checkMultipleEmails(emailsToCheck);
        emailStatuses = { ...emailStatuses, ...emailChecks };
      }

      const response = await api.post(`${this.baseUrl}/send`, {
        ...data,
        emailStatuses,
      });

      // If successful, trigger email sending
      if (response.data.success) {
        await this.triggerInvitationEmails(response.data.data.success);
      }

      return response.data;
    } catch (error: any) {
      console.error('Failed to send invitations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send invitations',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Get invitation details by token
   */
  async getInvitation(token: string): Promise<ApiResponse<ProjectInvitation>> {
    try {
      const response = await api.get(`${this.baseUrl}/token/${token}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get invitation:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Invitation not found or expired',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Respond to invitation (accept/decline)
   */
  async respondToInvitation(data: InvitationResponse): Promise<ApiResponse<void>> {
    try {
      const response = await api.post(`${this.baseUrl}/${data.invitationId}/respond`, {
        action: data.action,
        message: data.message,
      });

      // If accepted, trigger notification to project members
      if (response.data.success && data.action === 'accept') {
        await this.notifyInvitationAccepted(data.invitationId);
      }

      return response.data;
    } catch (error: any) {
      console.error('Failed to respond to invitation:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to respond to invitation',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Complete onboarding for new users
   */
  async completeOnboarding(data: OnboardingUserData): Promise<ApiResponse<User>> {
    try {
      // First, validate the invitation token
      const invitationResponse = await this.getInvitation(data.invitationToken);
      if (!invitationResponse.success || !invitationResponse.data) {
        return {
          success: false,
          message: 'Invalid or expired invitation',
        };
      }

      // Create user account and accept invitation
      const response = await api.post(`${this.baseUrl}/onboard`, {
        invitationToken: data.invitationToken,
        userData: {
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
          phone: data.phone,
          email: invitationResponse.data.email,
        },
      });

      if (response.data.success) {
        // Trigger welcome email and notifications
        await this.triggerWelcomeFlow(response.data.data, invitationResponse.data);
      }

      return response.data;
    } catch (error: any) {
      console.error('Failed to complete onboarding:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to complete onboarding',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Cancel invitation
   */
  async cancelInvitation(invitationId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`${this.baseUrl}/${invitationId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to cancel invitation:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to cancel invitation',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Resend invitation
   */
  async resendInvitation(invitationId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post(`${this.baseUrl}/${invitationId}/resend`);

      if (response.data.success) {
        // Update reminder count and trigger email
        await this.triggerReminderEmail(invitationId);
      }

      return response.data;
    } catch (error: any) {
      console.error('Failed to resend invitation:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to resend invitation',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Get all invitations for a project
   */
  async getProjectInvitations(projectId: string): Promise<ApiResponse<ProjectInvitation[]>> {
    try {
      const response = await api.get(`${this.baseUrl}/project/${projectId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get project invitations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get project invitations',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Check if email is available (user doesn't exist)
   */
  async checkEmailAvailability(
    email: string
  ): Promise<ApiResponse<{ available: boolean; userId?: string }>> {
    try {
      const response = await api.post('/auth/check-email', { email });
      return response.data;
    } catch (error: any) {
      console.error('Failed to check email availability:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to check email availability',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Get user's pending invitations
   */
  async getUserInvitations(): Promise<ApiResponse<ProjectInvitation[]>> {
    try {
      const response = await api.get(`${this.baseUrl}/user/pending`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get user invitations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get user invitations',
        errors: error.response?.data?.errors,
      };
    }
  }

  // Private helper methods

  /**
   * Check multiple emails for existing users
   */
  private async checkMultipleEmails(
    emails: string[]
  ): Promise<Record<string, { exists: boolean; userId?: string }>> {
    const results: Record<string, { exists: boolean; userId?: string }> = {};

    for (const email of emails) {
      try {
        const result = await this.checkEmailAvailability(email);
        results[email] = {
          exists: !result.data?.available,
          userId: result.data?.userId,
        };
      } catch {
        results[email] = { exists: false };
      }
    }

    return results;
  }

  /**
   * Trigger invitation emails
   */
  private async triggerInvitationEmails(invitations: ProjectInvitation[]): Promise<void> {
    try {
      for (const invitation of invitations) {
        const emailData: InvitationEmailData = {
          recipientEmail: invitation.email,
          projectName: invitation.projectName,
          role: invitation.role,
          inviterName: invitation.invitedByName,
          inviterEmail: '', // Will be filled by backend
          invitationToken: invitation.token,
          expiresAt: invitation.expiresAt,
          message: invitation.message,
          isNewUser: !invitation.metadata?.userExists,
        };

        await api.post('/emails/send-invitation', emailData);
      }
    } catch (error) {
      console.error('Failed to trigger invitation emails:', error);
    }
  }

  /**
   * Notify team about invitation acceptance
   */
  private async notifyInvitationAccepted(invitationId: string): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/${invitationId}/notify-accepted`);
    } catch (error) {
      console.error('Failed to notify invitation acceptance:', error);
    }
  }

  /**
   * Trigger welcome flow for new users
   */
  private async triggerWelcomeFlow(user: User, invitation: ProjectInvitation): Promise<void> {
    try {
      // Send welcome email
      await api.post('/emails/send-welcome', {
        user,
        projectName: invitation.projectName,
        role: invitation.role,
      });

      // Create welcome notification
      await api.post('/notifications', {
        userId: user.id,
        type: 'welcome',
        title: `Welcome to ${invitation.projectName}!`,
        message: `You've successfully joined the ${invitation.projectName} project as ${invitation.role}.`,
        metadata: {
          projectId: invitation.projectId,
          projectName: invitation.projectName,
          role: invitation.role,
        },
      });
    } catch (error) {
      console.error('Failed to trigger welcome flow:', error);
    }
  }

  /**
   * Trigger reminder email
   */
  private async triggerReminderEmail(invitationId: string): Promise<void> {
    try {
      await api.post(`/emails/send-invitation-reminder`, { invitationId });
    } catch (error) {
      console.error('Failed to trigger reminder email:', error);
    }
  }
}

// Export singleton instance
export const invitationService = new InvitationService();
